// MoviesGrid Component - Virtualized grid for displaying movies
import { useVirtualizer } from '@tanstack/react-virtual';
import React from 'react';
import { Movie } from '../../Movie';
import { useMediaQuery } from '../../hooks/useMediaQuery';

type GridSize = 'regular' | 'compact';

interface MoviesGridProps {
  movies: any[];
  showContent: boolean;
  size?: GridSize;
  columns?: number;
  static?: boolean; // Enable static mode without virtualization
  scrollElement?: React.RefObject<HTMLDivElement>; // Scrollable parent container
}

const GRID_CONFIG = {
  regular: {
    columnWidth: 300,
    estimateSize: {
      desktop: 601,
      mobile: 474,
    },
  },
  compact: {
    columnWidth: 220,
    estimateSize: {
      desktop: 320,
      mobile: 320,
    },
  },
};

const GUTTER = 16;

export function MoviesGrid({
  movies,
  showContent,
  size = 'regular',
  columns: fixedColumns,
  static: isStatic = false,
  scrollElement,
}: MoviesGridProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('(max-width:600px)');
  const config = GRID_CONFIG[size];
  const estimateSize = isMobile
    ? config.estimateSize.mobile
    : config.estimateSize.desktop;

  // Calculate columns based on container width or use fixed value
  const getColumnCount = () => {
    if (fixedColumns) return fixedColumns;
    if (!containerRef.current) return 1;
    const containerWidth = containerRef.current.clientWidth;
    return Math.max(
      1,
      Math.floor((containerWidth + GUTTER) / (config.columnWidth + GUTTER)),
    );
  };

  const [columnCount, setColumnCount] = React.useState(fixedColumns || 1);

  React.useEffect(() => {
    const columns = getColumnCount();
    setColumnCount(columns);

    if (!fixedColumns) {
      const handleResize = () => {
        setColumnCount(getColumnCount());
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [fixedColumns, isStatic]);

  // Static mode: no virtualization, just render all items
  if (isStatic) {
    const rowCount = Math.ceil(movies.length / columnCount);
    const rows = Array.from({ length: rowCount }).map((_, rowIndex) => {
      const startIndex = rowIndex * columnCount;
      const itemsInRow = Math.min(columnCount, movies.length - startIndex);
      return { rowIndex, startIndex, itemsInRow };
    });

    return (
      <div
        ref={containerRef}
        className={showContent ? 'fade-in' : ''}
        style={{
          width: '100%',
          opacity: showContent ? 1 : 0,
        }}
      >
        {rows.map(({ rowIndex, startIndex, itemsInRow }) => (
          <div
            key={rowIndex}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columnCount}, ${config.columnWidth}px)`,
              gap: `${GUTTER}px`,
              padding: '12px',
              justifyContent: 'center',
            }}
          >
            {Array.from({ length: itemsInRow }).map((_, i) => {
              const movieIndex = startIndex + i;
              const movie = movies[movieIndex];
              return (
                <Movie
                  key={movie?.id || movieIndex}
                  movie={movie}
                  size={size}
                />
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  // Virtualized mode
  const rowCount = Math.ceil(movies.length / columnCount);

  const virtualizer = useVirtualizer({
    count: rowCount,
    overscan: 5,
    estimateSize: () => estimateSize,
    getScrollElement: () => scrollElement?.current || document.documentElement,
  });

  const items = virtualizer.getVirtualItems();

  return (
    <div
      ref={containerRef}
      className={showContent ? 'fade-in' : ''}
      style={{
        width: '100%',
        opacity: showContent ? 1 : 0,
      }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {items.map((virtualItem) => {
          const startIndex = virtualItem.index * columnCount;
          const itemsInRow = Math.min(columnCount, movies.length - startIndex);

          return (
            <div
              key={virtualItem.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
                display: 'grid',
                gridTemplateColumns: `repeat(${columnCount}, ${config.columnWidth}px)`,
                gap: `${GUTTER}px`,
                padding: '12px',
                justifyContent: 'center',
              }}
            >
              {Array.from({ length: itemsInRow }).map((_, i) => {
                const movieIndex = startIndex + i;
                const movie = movies[movieIndex];
                return (
                  <Movie
                    key={movie?.id || movieIndex}
                    movie={movie}
                    size={size}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
